import React, { useCallback, useState } from 'react';
import { filterSupportedImageFiles } from '../util/imageFileFormat';

export const isFileDragEvent = (
  event: React.DragEvent<Element>,
): boolean => event.dataTransfer.types.includes('Files');

export const useImageFileDropState = <Target extends string>() => {
  const [activeDropTarget, setActiveDropTarget] = useState<Target | null>(null);

  const clearDropTarget = useCallback(() => {
    setActiveDropTarget(null);
  }, []);

  const handleDropTargetDragEnter = useCallback((
    event: React.DragEvent<Element>,
    target: Target,
    enabled: boolean,
  ): boolean => {
    if (!isFileDragEvent(event)) return false;
    event.preventDefault();
    event.stopPropagation();
    setActiveDropTarget(enabled ? target : null);
    return true;
  }, []);

  const handleFileDragOver = useCallback((
    event: React.DragEvent<Element>,
    enabled: boolean,
  ): boolean => {
    if (!isFileDragEvent(event)) return false;
    event.preventDefault();
    event.stopPropagation();
    event.dataTransfer.dropEffect = enabled ? 'copy' : 'none';
    return true;
  }, []);

  const handleFileDragLeave = useCallback((
    event: React.DragEvent<Element>,
  ): boolean => {
    const isFileDrag = isFileDragEvent(event);
    if (isFileDrag) {
      event.preventDefault();
      event.stopPropagation();
    }
    if (!event.currentTarget.contains(event.relatedTarget as Node)) {
      setActiveDropTarget(null);
    }
    return isFileDrag;
  }, []);

  const handleImageFileDrop = useCallback((
    event: React.DragEvent<Element>,
    target: Target,
    enabled: boolean,
    onDropImages: (files: File[], target: Target) => void | Promise<void>,
  ): boolean => {
    if (!isFileDragEvent(event)) return false;
    event.preventDefault();
    event.stopPropagation();
    setActiveDropTarget(null);
    if (!enabled) return true;

    const imageFiles = filterSupportedImageFiles(Array.from(event.dataTransfer.files));
    if (imageFiles.length > 0) {
      void onDropImages(imageFiles, target);
    }
    return true;
  }, []);

  return {
    activeDropTarget,
    clearDropTarget,
    handleDropTargetDragEnter,
    handleFileDragOver,
    handleFileDragLeave,
    handleImageFileDrop,
  };
};
